import { config } from '@/config'

import { type ApiError, ERROR_CODES, NetworkError, toApiError } from './errors'

/**
 * Client HTTP del pannello. Implementa le convenzioni trasversali del contract
 * (gymmi-contracts/docs/02-convenzioni.md) in un solo punto:
 *
 * - `Authorization: Bearer` con refresh **serializzato** su `access_token_expired`;
 * - `X-Organization-Id` con l'organizzazione selezionata (TEN-04/TEN-06);
 * - `ETag` / `If-Match` per la concorrenza ottimistica;
 * - `Idempotency-Key` sulle operazioni non idempotenti;
 * - `X-Request-Id` generato dal client, che il server propaga: una segnalazione
 *   utente diventa correlabile ai log anche quando la risposta si perde.
 */

export type QueryValue = string | number | boolean | undefined | null

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  query?: Record<string, QueryValue>
  body?: unknown
  /** `ETag` posseduto dal client. Il pannello lo manda sempre che può: più operatori
   *  lavorano sullo stesso catalogo. */
  ifMatch?: string | null
  /** UUID v4. Se assente su POST non idempotenti va passato esplicitamente. */
  idempotencyKey?: string
  signal?: AbortSignal
  /** `false` per gli endpoint pubblici (`/auth/login`, `/health`). */
  auth?: boolean
  /**
   * Bearer da usare invece di quello dello store. Serve solo per revocare una
   * sessione che non è stata applicata (login con ruolo insufficiente).
   */
  bearerToken?: string
  /** `false` per accettare come esito valido anche una risposta non-2xx. */
  throwOnError?: boolean
  /**
   * `false` per non allegare `X-Organization-Id`.
   *
   * Serve alle chiamate che **precedono** la scelta dell'organizzazione — su
   * tutte `GET /me`, che è ciò da cui il selettore ricava l'elenco: mandarle con
   * una selezione conservata e non più valida le farebbe fallire con `403`, e il
   * pannello perderebbe proprio l'informazione che gli serve per correggersi.
   */
  organization?: boolean
  /** Interno: evita il loop infinito di refresh. */
  _isRetry?: boolean
}

/** Risposta con l'`ETag` che l'accompagna, da rimandare in `If-Match`. */
export interface Tagged<T> {
  data: T
  etag: string | null
}

// ── ponte verso la sessione ────────────────────────────────────────────────────
//
// Il client non conosce lo store della sessione: lo store si registra qui. Evita un
// import circolare e rende il client testabile senza React.

export interface SessionBridge {
  getAccessToken: () => string | null
  /**
   * Ottiene un access token nuovo. **Deve** essere single-flight: refresh
   * concorrenti fanno scattare il rilevamento di riuso lato server e
   * disconnettono l'utente (docs/03-autenticazione.md).
   */
  refresh: () => Promise<string | null>
  /** La sessione non è recuperabile: riportare al login. */
  onSessionLost: (error: ApiError) => void
}

let bridge: SessionBridge = {
  getAccessToken: () => null,
  refresh: () => Promise.resolve(null),
  onSessionLost: () => {},
}

export function configureSessionBridge(next: SessionBridge): void {
  bridge = next
}

// ── ponte verso l'organizzazione selezionata (TEN-06) ─────────────────────────
//
// Stesso schema del ponte di sessione, e per lo stesso motivo: l'header va messo
// in `send()`, che non è un componente e non può leggere uno store con un hook.

export interface TenantBridge {
  /** `null` = nessuna selezione: decide il server (organizzazione personale). */
  getOrganizationId: () => string | null
  /** L'organizzazione selezionata non è più utilizzabile: va deselezionata. */
  onOrganizationRejected: (error: ApiError) => void
}

let tenantBridge: TenantBridge = {
  getOrganizationId: () => null,
  onOrganizationRejected: () => {},
}

export function configureTenantBridge(next: TenantBridge): void {
  tenantBridge = next
}

// ── utilità ───────────────────────────────────────────────────────────────────

export function newUuid(): string {
  return crypto.randomUUID()
}

function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  const url = `${config.apiBaseUrl}${path.startsWith('/') ? path : `/${path}`}`
  if (!query) return url

  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    // `undefined` e `null` significano "filtro non applicato": non vanno in query
    // string, altrimenti `published=` filtrerebbe per stringa vuota.
    if (value === undefined || value === null || value === '') continue
    params.set(key, String(value))
  }
  const qs = params.toString()
  return qs ? `${url}?${qs}` : url
}

// ── richiesta ─────────────────────────────────────────────────────────────────

async function send(path: string, options: RequestOptions): Promise<Response> {
  const {
    method = 'GET',
    query,
    body,
    ifMatch,
    idempotencyKey,
    signal,
    auth = true,
    bearerToken,
    organization = true,
  } = options

  const headers = new Headers({
    Accept: 'application/json',
    'X-Request-Id': newUuid(),
  })

  /**
   * `FormData` **non** prende un `Content-Type` da noi: lo scrive il browser, con il
   * `boundary` che solo lui conosce. Scriverlo a mano produce un multipart che il
   * server non sa spezzare, e l'errore che ne segue parla di campo mancante.
   */
  const isMultipart = typeof FormData !== 'undefined' && body instanceof FormData
  if (body !== undefined && !isMultipart) {
    headers.set('Content-Type', 'application/json; charset=utf-8')
  }
  if (ifMatch) headers.set('If-Match', ifMatch)
  if (idempotencyKey) headers.set('Idempotency-Key', idempotencyKey)

  if (auth) {
    const token = bearerToken ?? bridge.getAccessToken()
    if (token) headers.set('Authorization', `Bearer ${token}`)

    // L'header si manda **solo** quando una scelta esplicita c'è. Senza, il
    // server applica la sua cascata (sottodominio, claim `org`, area personale):
    // mandare un valore inventato o vuoto significherebbe sostituire quella
    // cascata con una peggiore, e un id non-UUID è `400` per scelta del backend.
    const organizationId = organization ? tenantBridge.getOrganizationId() : null
    if (organizationId) headers.set('X-Organization-Id', organizationId)
  }

  try {
    return await fetch(buildUrl(path, query), {
      method,
      headers,
      ...(body !== undefined ? { body: isMultipart ? body : JSON.stringify(body) } : {}),
      ...(signal ? { signal } : {}),
      // Necessario se il refresh token viaggia in cookie HttpOnly.
      credentials: config.refreshTransport === 'cookie' ? 'include' : 'same-origin',
    })
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
    throw new NetworkError(
      'Impossibile raggiungere il server. Controlla la connessione e riprova.',
      cause,
    )
  }
}

/**
 * Esegue la richiesta e, su `401 access_token_expired`, fa un solo tentativo di
 * refresh prima di ripetere. Un secondo 401 dopo il refresh significa che la
 * sessione è persa davvero: si notifica lo store e si propaga l'errore.
 */
async function sendWithRefresh(path: string, options: RequestOptions): Promise<Response> {
  const response = await send(path, options)
  if (response.ok || response.status !== 401 || options.auth === false || options._isRetry) {
    return response
  }

  const error = await toApiError(response)

  if (error.isSessionLost) {
    bridge.onSessionLost(error)
    throw error
  }

  // `unauthorized` (header assente) è recuperabile come `access_token_expired`:
  // in entrambi i casi un token valido risolve, se c'è ancora un refresh spendibile.
  if (error.code !== ERROR_CODES.accessTokenExpired && error.code !== ERROR_CODES.unauthorized) {
    throw error
  }

  const token = await bridge.refresh()
  if (!token) {
    bridge.onSessionLost(error)
    throw error
  }

  const retried = await send(path, { ...options, _isRetry: true })

  // Un 401 anche **dopo** un refresh riuscito: il token nuovo non è accettato — chiave
  // di firma ruotata, jti invalidati, account disabilitato nel frattempo. Non c'è
  // altro da tentare, quindi la sessione è persa e va notificata: senza, l'operatore
  // resterebbe su una pagina che risponde «Accesso richiesto» a ogni azione.
  if (retried.status === 401) {
    const finalError = await toApiError(retried)
    bridge.onSessionLost(finalError)
    throw finalError
  }

  return retried
}

/**
 * Costruisce l'errore da una risposta fallita, segnalando al ponte del tenant i
 * `403` che riguardano l'organizzazione selezionata.
 *
 * La segnalazione sta qui e non nei singoli chiamanti perché la selezione è
 * trasversale: una qualunque richiesta può essere la prima a scoprire che
 * l'appartenenza è finita, e chiedere a ogni hook di ricordarsene sarebbe un
 * controllo da dimenticare in almeno un punto.
 */
async function toRejectedError(response: Response): Promise<ApiError> {
  const error = await toApiError(response)
  if (error.isOrganizationRejected) tenantBridge.onOrganizationRejected(error)
  return error
}

async function parseBody<T>(response: Response): Promise<T> {
  // 204 e 304 non hanno corpo.
  if (response.status === 204 || response.status === 304) return undefined as T
  const text = await response.text()
  if (!text) return undefined as T
  try {
    return JSON.parse(text) as T
  } catch (cause) {
    throw new NetworkError('Risposta del server non interpretabile.', cause)
  }
}

/**
 * Richiesta che restituisce solo il corpo deserializzato.
 *
 * Con `throwOnError: false` una risposta non-2xx **non** diventa un'eccezione e il
 * corpo viene restituito comunque. Serve dove il contract usa un codice di errore per
 * trasportare un esito legittimo: `GET /health/ready` risponde `503` con lo stesso
 * schema `Readiness` del `200`, e trattarlo come errore butterebbe via l'unica cosa
 * utile — quale dipendenza è giù.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await sendWithRefresh(path, options)
  if (!response.ok && options.throwOnError !== false) throw await toRejectedError(response)
  return parseBody<T>(response)
}

/**
 * Come `request`, ma restituisce anche l'`ETag`.
 *
 * Perché l'`ETag` possa arrivare fin qui, il backend deve elencarlo in
 * `Access-Control-Expose-Headers`: senza, JavaScript non lo vede e il controllo di
 * concorrenza non funziona dal browser (docs/08-api-admin.md). Se manca, `etag` è
 * `null` e le modifiche procedono senza `If-Match` — ultima scrittura vince.
 */
export async function requestTagged<T>(
  path: string,
  options: RequestOptions = {},
): Promise<Tagged<T>> {
  const response = await sendWithRefresh(path, options)
  if (!response.ok) throw await toRejectedError(response)
  return {
    data: await parseBody<T>(response),
    etag: response.headers.get('ETag'),
  }
}

/**
 * Come `request`, ma restituisce il **file** e il nome con cui salvarlo.
 *
 * Serve alle due esportazioni dell'anagrafica (ANA-08): un CSV e un JSON di portabilità non
 * si mostrano in pagina, si scaricano. Il nome arriva da `Content-Disposition` e non è un
 * dettaglio — porta la data, così due esportazioni a un mese di distanza non finiscono nella
 * cartella dei download con lo stesso nome. Se l'header non è esposto in CORS si ricade sul
 * nome passato dal chiamante, che è meno preciso ma non blocca lo scaricamento.
 */
export async function requestFile(
  path: string,
  fallbackFilename: string,
  options: RequestOptions = {},
): Promise<{ blob: Blob; filename: string }> {
  const response = await sendWithRefresh(path, options)
  if (!response.ok) throw await toRejectedError(response)

  const disposition = response.headers.get('Content-Disposition') ?? ''
  const match = /filename="?([^";]+)"?/.exec(disposition)
  return { blob: await response.blob(), filename: match?.[1] ?? fallbackFilename }
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),

  getTagged: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    requestTagged<T>(path, { ...options, method: 'GET' }),

  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),

  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),

  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),

  delete: <T = void>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),

  download: (
    path: string,
    fallbackFilename: string,
    options?: Omit<RequestOptions, 'method' | 'body'>,
  ) => requestFile(path, fallbackFilename, { ...options, method: 'GET' }),
}
